import { createSlice } from "@reduxjs/toolkit";
import { getMonthsAgo } from "helpers/dates";
import { isBlank } from "helpers/strings";
import { DateObject } from "react-multi-date-picker";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import {
  DefaultFilterState,
  DateRadio,
  DefaultInputFilter,
  DefaultFilterType,
  DefaultUnionFilter,
  DefaultCompanyFilter
} from 'redux/global.consts';

export const reportingFilterSlice = createSlice({
  name: "reportingFilter",
  initialState: Object.assign({}, DefaultFilterState),
  reducers: {
    goToPage: (state, data) => {
      state.pageOffset = data.payload;
    },
    pickFromDate: (state, data) => {
      state.pickedDateFrom = data.payload;
      if (state.selectedRadio === DateRadio.CustomDate) {
        state.fromDate = data.payload;
      }
    },
    pickToDate: (state, data) => {
      state.pickedDateTo = data.payload;
      if (state.selectedRadio === DateRadio.CustomDate) {
        state.toDate = data.payload;
      }
    },
    handleInputChange: (state, data) => {
      state.selectedInput = data.payload;
    },
    handleFilterTypeChange: (state, data) => {
      state.selectedFilterType = data.payload;
    },
    handleUnionFilterChange: (state, data) => {
      state.selectedUnion = data.payload;
    },
    handleCompanyFilterChange: (state, data) => {
      state.selectedCompany = data.payload;
    },
    handleDateRadioChange: (state, data) => {
      const radioId = data.payload;

      state.selectedRadio = radioId;

      let from, to;
      switch (radioId) {
        case DateRadio.LastMonth:
          from = getMonthsAgo(1).toISOString();
          to = new Date().toISOString();
          break;
        case DateRadio.LastThreeMonths:
          from = getMonthsAgo(3).toISOString();
          to = new Date().toISOString();
          break;
        case DateRadio.CustomDate:
          from = state.pickedDateFrom;
          to = state.pickedDateTo;
          break;
        default:
        //stuff
      }

      state.fromDate = from;
      state.toDate = to;
    },
    buildFiltersQuery: (state, _) => {
      let buysReportFilter = '';
      let distsReportFilter = '';
      let productsReportFilter = '';
      let storesReportFilter = '';

      if (state.pageOffset > 1) {
        buysReportFilter += `page=${state.pageOffset}&`;
        distsReportFilter += `page=${state.pageOffset}&`;
        productsReportFilter += `page=${state.pageOffset}&`;
      }

      if (!isBlank(state.fromDate)) {
        const formattedString = new DateObject(new Date(state.fromDate))
          .convert(gregorian, gregorian_en)
          .toUTC()
          .format("YYYY-MM-DD");

        buysReportFilter += `start_date=${formattedString}&`;
        distsReportFilter += `start_date=${formattedString}&`;
        productsReportFilter += `start_date=${formattedString}&`;
      }
      if (!isBlank(state.toDate)) {
        const formattedString = new DateObject(new Date(state.toDate))
          .convert(gregorian, gregorian_en)
          .toUTC()
          .format("YYYY-MM-DD");

        buysReportFilter += `end_date=${formattedString}&`;
        distsReportFilter += `end_date=${formattedString}&`;
        productsReportFilter += `end_date=${formattedString}&`;
      }
      if (state.selectedInput.value > 0) {
        buysReportFilter += `input_id=${state.selectedInput.value}&`;
        distsReportFilter += `input_id=${state.selectedInput.value}&`;
        productsReportFilter += `input_id=${state.selectedInput.value}&`;
        storesReportFilter += `input_id=${state.selectedInput.value}&`;
      }
      if (state.selectedFilterType) {
        switch (parseInt(state.selectedFilterType.value)) {
          //Filter slef items (self is current logged-in union)
          case -2:
            // buysReportFilter += `all_items=false&`;
            // distsReportFilter += `all_items=false&`;
            // productsReportFilter += `all_items=false&`;
            // storesReportFilter += `all_items=false&`;
            break;
          //Filter all items
          case -1:
            buysReportFilter += `all_items=true&`;
            distsReportFilter += `all_items=true&`;
            productsReportFilter += `all_items=true&`;
            storesReportFilter += `all_items=true&`;
            break;
          //Filter company items
          case 0:
            const compsFilter = state.selectedCompany.value > 0
              ? `coop_id=${state.selectedCompany.value}&`
              : `all_items=true&`;

            buysReportFilter += compsFilter;
            distsReportFilter += compsFilter;
            productsReportFilter += compsFilter;
            storesReportFilter += compsFilter;
            break;
          //Filter union items
          case 1:
            const unionsFilter = state.selectedUnion.value > 0
              ? `union_id=${state.selectedUnion.value}&`
              : `all_items=true&`;

            buysReportFilter += unionsFilter;
            distsReportFilter += unionsFilter;
            productsReportFilter += unionsFilter;
            storesReportFilter += unionsFilter;
            break;
          default:
          //nothing
        }
      }

      state.buysReportFilter = `?${buysReportFilter}`;
      state.distsReportFilter = `?${distsReportFilter}`;
      state.productsReportFilter = `?${productsReportFilter}`;
      state.storesReportFilter = `?${storesReportFilter}`;
    },
    resetAllFilters: (state, _) => {
      const from = new DateObject(getMonthsAgo(1))
        .convert(gregorian, gregorian_en)
        .toUTC()
        .format("YYYY-MM-DD");

      const to = new DateObject(new Date())
        .convert(gregorian, gregorian_en)
        .toUTC()
        .format("YYYY-MM-DD");

      state.goToPage = 1;
      state.fromDate = from;
      state.toDate = to;
      state.selectedInput = DefaultInputFilter;
      state.selectedFilterType = DefaultFilterType;
      state.selectedUnion = DefaultUnionFilter;
      state.selectedCompany = DefaultCompanyFilter;
      state.pickedDateFrom = from;
      state.pickedDateTo = to;
      state.selectedRadio = DateRadio.LastMonth;
      state.buysReportFilter = `?start_date=${from}&end_date=${to}&all_items=true&`;
      state.distsReportFilter = `?start_date=${from}&end_date=${to}&all_items=true&`;
      state.productsReportFilter = `?start_date=${from}&end_date=${to}&all_items=true&`;
      state.storesReportFilter = `?all_items=true&`;
    },
  },
});

export const {
  goToPage,
  handleDateRadioChange,
  pickFromDate,
  pickToDate,
  handleInputChange,
  handleUnionFilterChange,
  handleCompanyFilterChange,
  handleFilterTypeChange,
  buildFiltersQuery,
  resetAllFilters
} = reportingFilterSlice.actions;
export default reportingFilterSlice.reducer;
